<template>
  <Layout
    class="onboarding-welcome16167"
    :class="`onboarding-welcome16167--${currentKeyframe}`"
    :title="page.title"
    :subtitle="page.subtitle"
  >
    <Welcome16167Intro
      :title="page.introTitle"
      class="onboarding-welcome16167__intro"
    />
    <img
      v-if="currentKeyframe !== 'intro'"
      :src="getImage(page.image.src)"
      :width="page.image.width"
      :height="page.image.height"
      class="onboarding-welcome16167__mascot"
    />

    <!-- We mount the Rive component here to preload its .wasm dependency. -->
    <Rive
      v-show="false"
      class="onboarding-welcome16167__rive"
      :src="getImage('preload.riv')"
    />
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { getImage, QuestionnaireView } from '@/models/onboarding'
import { Welcome16167Page } from '@/models/onboarding.types'
import { PublicInterface } from '@/models/interfaces'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'
import Welcome16167Intro from '../Welcome16167Intro.vue'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const page = computed(() => {
  return props.view.currentPage as Welcome16167Page
})

const animationKeyframes = {
  intro: 0,
  showMascot: 2500,
  updateText: 3000,
  showButton: 5200,
}
const currentKeyframe = ref<keyof typeof animationKeyframes>('intro')

onMounted(() => {
  setTimeout(() => {
    currentKeyframe.value = 'showMascot'
    props.view.showLogo()
  }, animationKeyframes.showMascot)

  setTimeout(() => {
    currentKeyframe.value = 'updateText'
  }, animationKeyframes.updateText)

  setTimeout(() => {
    currentKeyframe.value = 'showButton'
    props.view.showContinueButton()
  }, animationKeyframes.showButton)
})
</script>

<style lang="scss" scoped>
.onboarding-welcome16167 {
  max-height: 900px;
  margin-block: auto;

  :deep(.onboarding-layout__text) {
    flex: 1;
    justify-content: flex-end;
    margin-bottom: 24px;
  }

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    min-height: 0;
    margin-bottom: calc(var(--onboarding-padding) * -2);
    @media (min-width: $w-tablet-b) and (min-height: 900px) {
      margin-bottom: var(--onboarding-padding);
    }
  }

  :deep(.onboarding-layout__text) {
    gap: 24px;
  }

  :deep(.onboarding-layout__title) {
    font-size: 32px;
    line-height: 36px;
  }

  :deep(.onboarding-layout__subtitle) {
    font-size: 28px;
    line-height: 34px;
    font-weight: 500;
    color: $c-black-2;

    span {
      color: #5aa96b;
    }
  }

  &__mascot {
    flex-shrink: 0;
    width: 400px;
    max-width: calc(100% + var(--onboarding-padding) * 2);
    max-height: 100%;
    transform: scale(0.5);
    transform-origin: bottom;
    animation:
      mascot-scale-up 0.5s forwards,
      fadeIn 0.3s ease-out forwards;
  }
}

.onboarding-welcome16167--intro {
  :deep(.onboarding-layout__default-slot) {
    flex: 1;
  }
}

.onboarding-welcome16167--intro,
.onboarding-welcome16167--showMascot {
  :deep(.onboarding-layout__text) {
    visibility: hidden;
  }
}

.onboarding-welcome16167--updateText,
.onboarding-welcome16167--showButton {
  :deep(.onboarding-layout__title) {
    animation: fadeIn 1.3s 0.1s both;
  }

  :deep(.onboarding-layout__subtitle) {
    animation: fadeIn 1.3s 1.2s both;
  }
}

.onboarding-welcome16167--showButton {
  ~ :deep(.onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
    backdrop-filter: blur(2px);
    animation: fadeIn 0.7s both;

    &:after {
      opacity: 0.8;
    }
  }
}

@keyframes mascot-scale-up {
  from {
    transform: scale(0.5);
  }
  to {
    transform: scale(1);
  }
}
</style>
